import React, { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import { useIntl } from 'react-intl-next';
import debouncePromise from 'debounce-promise';
import { utils } from '@atlaskit/util-service-support';
import { TextLabelWrapper, TextCorrectionWrapper } from './autocorrection.styled';
import { messages } from '../../messages';
import { useAnalytics } from '../../common/analytics/use-analytics';
import { sha256Hash } from '../../common/analytics/utils';
import { onAutocorrectClicked, onAutocorrectShown } from '../../common/analytics/events';
import { AGGREGATOR_URL } from '../../common/search-config-provider/use-search-config-client';
import {
	sanitizeLanguage,
	shouldFetchAutocorrection,
	isSupportedLanguage,
	AUTOCORRECT_API_PATH,
	DEFAULT_AUTOCORRECT_DEBOUNCE_TIME_MS,
} from './autocorrection-helper-functions';

type correctionType = {
	term: string;
	distance: number;
};

type AutocorrectionTextProps = {
	label: string;
	onClick: () => void;
	corrections: correctionType[];
};

export const AutocorrectionText = ({ label, onClick, corrections }: AutocorrectionTextProps) => {
	return (
		<Fragment>
			<TextLabelWrapper>{label}: </TextLabelWrapper>
			<TextCorrectionWrapper onClick={onClick}>
				{corrections.map(({ distance, term }, index) => {
					const space = index === corrections.length - 1 ? '' : ' ';
					if (distance === 0) {
						return term.concat(space);
					} else {
						return (
							<React.Fragment>
								<b>{term}</b>
								{space}
							</React.Fragment>
						);
					}
				})}
			</TextCorrectionWrapper>
		</Fragment>
	);
};

type AutocorrectionComponentProps = {
	aggregatorUrl: string;
	cloudId: string;
	query: string;
	label: string;
	lang: string;
	onCorrectionSelect: (correction: string) => void;
	source?: string;
	searchSessionId?: string;
	productId?: string;
	debounce?: number;
	noCorrectionPlaceholder?: JSX.Element;
};

const AutocorrectionComponent = ({
	aggregatorUrl,
	cloudId,
	query,
	label,
	lang,
	onCorrectionSelect,
	source = '',
	searchSessionId = '',
	productId = '',
	debounce = DEFAULT_AUTOCORRECT_DEBOUNCE_TIME_MS,
	noCorrectionPlaceholder,
}: AutocorrectionComponentProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const [corrections, setCorrections] = useState<correctionType[]>([]);
	const correction = corrections.map((word) => word.term).join(' ');
	const shouldFetchCorrections = shouldFetchAutocorrection(query);

	const getCorrection = useRef(
		debouncePromise((query: string) => {
			return utils.requestService<any>(
				{ url: aggregatorUrl },
				{
					path: `${AUTOCORRECT_API_PATH}`,
					requestInit: {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							query: query,
							language: lang,
							cloudId: cloudId,
						}),
					},
				},
			);
		}, debounce),
	).current;

	const onClick = useCallback(() => {
		setCorrections([]);
		onCorrectionSelect(correction);
		fireAnalyticsEvent(
			onAutocorrectClicked({
				source: source,
				attributes: {
					searchSessionId: searchSessionId,
					correctionHash: sha256Hash(correction),
					language: lang,
					productId: productId,
				},
			}),
		);
	}, [
		onCorrectionSelect,
		correction,
		fireAnalyticsEvent,
		lang,
		productId,
		searchSessionId,
		source,
	]);

	useEffect(() => {
		setCorrections([]);
		if (shouldFetchCorrections) {
			getCorrection(query).then((result) => {
				if (!!result.termCorrections) {
					setCorrections(result.termCorrections);
				}
			});
		}
	}, [shouldFetchCorrections, query, getCorrection]);

	useEffect(() => {
		if (corrections.length > 0 && shouldFetchCorrections) {
			fireAnalyticsEvent(
				onAutocorrectShown({
					source: source,
					attributes: {
						searchSessionId: searchSessionId,
						correction: sha256Hash(correction),
						language: lang,
						productId: productId,
					},
				}),
			);
		}
	}, [
		shouldFetchCorrections,
		corrections,
		correction,
		fireAnalyticsEvent,
		lang,
		productId,
		query,
		searchSessionId,
		source,
	]);

	if (corrections.length === 0 || !query) {
		return !!noCorrectionPlaceholder ? noCorrectionPlaceholder : null;
	}

	return <AutocorrectionText label={label} onClick={onClick} corrections={corrections} />;
};

export type AutocorrectionProps = Omit<
	AutocorrectionComponentProps,
	'label' | 'lang' | 'aggregatorUrl'
> & {
	aggregatorUrl?: string;
	lang?: string;
	container?: React.ComponentType<React.PropsWithChildren<{}>>;
};

/**
 * This component is a standalone autocorrection component that contains the supplier and renderer in the same component
 * Used in Confluence Advanced Search
 */
export const Autocorrection = ({
	lang: propsLang,
	aggregatorUrl = AGGREGATOR_URL,
	container,
	...rest
}: AutocorrectionProps) => {
	const { formatMessage, locale } = useIntl();
	const intlLang = sanitizeLanguage(locale);
	const lang = propsLang || intlLang;

	if (!isSupportedLanguage(lang)) {
		return null;
	}
	const Wrapper = !!container ? container : (props: any) => <div {...props} />;
	return (
		<Wrapper data-testid="autocorrect-wrapper">
			<AutocorrectionComponent
				label={formatMessage(messages.common_autocorrection_label)}
				lang={lang}
				aggregatorUrl={aggregatorUrl}
				{...rest}
			/>
		</Wrapper>
	);
};
